/**
 * Date formatting helpers for BI chart axis labels and pie slices.
 *
 * Detects ISO-8601 date strings and converts them to friendly UK format.
 * Granularity-aware: formats differently for day / week / month / year groupings.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s)/;

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Get the ISO week number for a date.
 */
function isoWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export type DateGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * Format a single category value. If it looks like an ISO date, convert it
 * to a friendly string based on the active granularity. Non-date strings
 * are returned as-is.
 */
export function formatCategory(value: string, granularity?: DateGranularity): string {
  if (!ISO_DATE_RE.test(value)) return value;

  const d = new Date(value);
  if (isNaN(d.getTime())) return value;

  switch (granularity) {
    case 'year':
      return String(d.getUTCFullYear());
    case 'quarter': {
      const q = Math.floor(d.getUTCMonth() / 3) + 1;
      return `Q${q} ${d.getUTCFullYear()}`;
    }
    case 'month':
      return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
    case 'week':
      return `Wk ${isoWeek(d)}, ${d.getUTCFullYear()}`;
    case 'day':
    default:
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  }
}

/**
 * Format an array of category values (convenience wrapper).
 */
export function formatCategories(values: string[], granularity?: DateGranularity): string[] {
  return values.map((v) => formatCategory(v, granularity));
}
